import * as pc from 'playcanvas';
import type { WorldFlythrough } from '@viewer/world/worldTypes';
import type { PlayCanvasWorldContent } from '@viewer/playcanvas/PlayCanvasWorldContent';
import {
  collectDueFlythroughCues,
  resolveFlythroughChapters,
  resolveFlythroughDurationMs,
  sampleFlythrough,
  type FlythroughSample,
  type ResolvedFlythroughChapter,
} from '@viewer/playcanvas/flythroughPath';

interface SavedCameraPose {
  position: pc.Vec3;
  rotation: pc.Quat;
  fov?: number;
}

interface CameraReturnPose {
  position: pc.Vec3;
  rotation: pc.Quat;
  fov?: number;
}

type FlythroughPlaybackState = 'playing' | 'paused' | 'returning' | 'complete';
type ReturnMode = 'play' | 'pause';

interface ActiveFlythrough {
  tour: WorldFlythrough;
  state: FlythroughPlaybackState;
  lastTickMs: number;
  elapsedMs: number;
  durationMs: number;
  firedCues: Set<string>;
  savedPose: SavedCameraPose;
  returnFrom?: CameraReturnPose;
  returnStartedAtMs?: number;
  returnDurationMs?: number;
  returnMode?: ReturnMode;
  freeExploring?: boolean;
}

interface FlythroughControllerOptions {
  root: HTMLElement;
  cameraEntity: pc.Entity;
  flythroughs: WorldFlythrough[];
  getWorldContent: () => PlayCanvasWorldContent | null;
  requestRender: (settleMs?: number) => void;
  syncLookFromEntity?: () => void;
  onActiveChange?: (active: boolean) => void;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export class PlayCanvasFlythroughController {
  private readonly cameraEntity: pc.Entity;
  private readonly flythroughs: WorldFlythrough[];
  private readonly getWorldContent: () => PlayCanvasWorldContent | null;
  private readonly requestRender: (settleMs?: number) => void;
  private readonly syncLookFromEntity?: () => void;
  private readonly onActiveChange?: (active: boolean) => void;
  private readonly root: HTMLDivElement;
  private readonly toggleBtn: HTMLButtonElement;
  private readonly menu: HTMLDivElement;
  private readonly menuHeading: HTMLDivElement;
  private readonly menuList: HTMLDivElement;
  private readonly titleEl: HTMLDivElement;
  private readonly status: HTMLDivElement;
  private readonly stateLabel: HTMLSpanElement;
  private readonly playBtn: HTMLButtonElement;
  private readonly exitBtn: HTMLButtonElement;
  private readonly chaptersEl: HTMLDivElement;
  private readonly progress: HTMLDivElement;
  private readonly pointerHint: HTMLDivElement;
  private active: ActiveFlythrough | null = null;
  private chromeVisible = false;
  private menuOpen = false;
  private pointerLocked = false;
  private photoModeActive = false;
  private renderedChapterKey = '';
  private launcherEnterTimer: number | null = null;
  private launcherEntering = false;

  private static readonly RETURN_DURATION_MS = 950;

  constructor(options: FlythroughControllerOptions) {
    this.cameraEntity = options.cameraEntity;
    this.flythroughs = options.flythroughs;
    this.getWorldContent = options.getWorldContent;
    this.requestRender = options.requestRender;
    this.syncLookFromEntity = options.syncLookFromEntity;
    this.onActiveChange = options.onActiveChange;

    PlayCanvasFlythroughController.ensureStyles();
    this.root = document.createElement('div');
    this.root.className = 'gii-flythrough-ui gii-viewer-chrome gii-viewer-chrome--hidden';
    this.root.innerHTML = `
      <div class="gii-flythrough-island">
        <button type="button" class="gii-flythrough-toggle" aria-haspopup="menu" aria-expanded="false" aria-label="Guided tours" title="Guided tours">
          ${PlayCanvasFlythroughController.iconTours()}
        </button>
        <div class="gii-flythrough-menu" role="menu" aria-hidden="true">
          <div class="gii-flythrough-menu-heading" role="button" tabindex="0" aria-label="Close guided tours menu">GUIDED TOURS</div>
          <div class="gii-flythrough-menu-list"></div>
        </div>
      </div>
      <div class="gii-flythrough-title" hidden></div>
      <div class="gii-flythrough-status" aria-live="polite" hidden>
        <span class="gii-flythrough-state"></span>
        <div class="gii-flythrough-controls">
          <button type="button" class="gii-flythrough-play" title="Pause tour">Pause</button>
          <div class="gii-flythrough-timeline" aria-label="Tour timeline">
            <span class="gii-flythrough-progress"><span></span></span>
            <div class="gii-flythrough-chapters"></div>
          </div>
          <button type="button" class="gii-flythrough-exit" title="Exit tour">Exit</button>
        </div>
        <div class="gii-flythrough-pointer-hint" aria-hidden="true">PRESS 'ESC' TO RELEASE MOUSE & USE BUTTONS</div>
      </div>
    `;
    this.toggleBtn = this.root.querySelector('.gii-flythrough-toggle') as HTMLButtonElement;
    this.menu = this.root.querySelector('.gii-flythrough-menu') as HTMLDivElement;
    this.menuHeading = this.root.querySelector('.gii-flythrough-menu-heading') as HTMLDivElement;
    this.menuList = this.root.querySelector('.gii-flythrough-menu-list') as HTMLDivElement;
    this.titleEl = this.root.querySelector('.gii-flythrough-title') as HTMLDivElement;
    this.status = this.root.querySelector('.gii-flythrough-status') as HTMLDivElement;
    this.stateLabel = this.root.querySelector('.gii-flythrough-state') as HTMLSpanElement;
    this.playBtn = this.root.querySelector('.gii-flythrough-play') as HTMLButtonElement;
    this.exitBtn = this.root.querySelector('.gii-flythrough-exit') as HTMLButtonElement;
    this.chaptersEl = this.root.querySelector('.gii-flythrough-chapters') as HTMLDivElement;
    this.progress = this.root.querySelector('.gii-flythrough-progress span') as HTMLDivElement;
    this.pointerHint = this.root.querySelector('.gii-flythrough-pointer-hint') as HTMLDivElement;
    options.root.appendChild(this.root);

    this.toggleBtn.addEventListener('click', () => {
      if (this.active) {
        this.stop(true);
        return;
      }
      this.setMenuOpen(!this.menuOpen);
    });
    this.menuHeading.addEventListener('click', () => this.setMenuOpen(false));
    this.menuHeading.addEventListener('keydown', (event) => {
      if (event.code !== 'Enter' && event.code !== 'Space') return;
      event.preventDefault();
      this.setMenuOpen(false);
    });
    this.playBtn.addEventListener('click', () => this.handlePlayButton());
    this.exitBtn.addEventListener('click', () => this.stop(true));
    this.renderMenu();
    this.syncUi();
  }

  isActive(): boolean {
    return this.active !== null;
  }

  isCameraLocked(): boolean {
    return this.active?.state === 'playing' || this.active?.state === 'returning';
  }

  pauseForWorldInteraction(): boolean {
    if (!this.isCameraLocked()) return false;
    this.pause(true);
    return true;
  }

  setChromeVisible(visible: boolean): void {
    const nextVisible = visible && this.flythroughs.length > 0;
    if (this.chromeVisible === nextVisible) return;
    this.chromeVisible = nextVisible;
    this.syncUi();
  }

  setPointerLocked(locked: boolean): void {
    this.pointerLocked = locked;
    if (this.active?.state === 'paused') {
      this.active.freeExploring = locked && !this.photoModeActive;
    }
    this.syncUi();
  }

  setPhotoModeActive(active: boolean): void {
    this.photoModeActive = active;
    if (active && this.active) {
      this.active.state = 'paused';
      this.active.freeExploring = false;
      this.active.returnFrom = undefined;
      this.active.returnStartedAtMs = undefined;
      this.active.returnDurationMs = undefined;
      this.active.returnMode = undefined;
      this.syncLookFromEntity?.();
      this.requestRender(1);
    }
    this.syncUi();
  }

  tick(nowMs: number): void {
    if (!this.active) return;
    const active = this.active;

    if (active.state === 'paused' || active.state === 'complete') return;

    if (active.state === 'returning') {
      const startedAt = active.returnStartedAtMs ?? nowMs;
      const duration = active.returnDurationMs ?? PlayCanvasFlythroughController.RETURN_DURATION_MS;
      const from = active.returnFrom ?? this.readCameraPose();
      const blendElapsedMs = nowMs - startedAt;
      const target = sampleFlythrough(active.tour, active.elapsedMs);
      this.applyReturnMorph(from, target, blendElapsedMs, duration);
      this.updateProgress();
      this.requestRender(1);
      if (blendElapsedMs >= duration) {
        active.returnFrom = undefined;
        active.returnStartedAtMs = undefined;
        active.returnDurationMs = undefined;
        const mode = active.returnMode ?? 'play';
        active.returnMode = undefined;
        active.freeExploring = false;
        active.state = mode === 'play' ? 'playing' : 'paused';
        active.lastTickMs = nowMs;
        this.applySample(active.tour, active.elapsedMs);
        this.syncLookFromEntity?.();
        this.syncUi();
      }
      return;
    }

    const previousMs = active.elapsedMs;
    const deltaMs = Math.max(0, nowMs - active.lastTickMs);
    active.lastTickMs = nowMs;
    active.elapsedMs = Math.min(active.durationMs, active.elapsedMs + deltaMs);
    this.applySample(active.tour, active.elapsedMs);

    for (const cue of collectDueFlythroughCues(
      active.tour,
      previousMs,
      active.elapsedMs,
      active.firedCues,
    )) {
      this.getWorldContent()?.runAction(cue.action);
    }

    this.updateProgress();
    this.requestRender(1);
    if (active.elapsedMs >= active.durationMs) {
      if (active.tour.onComplete) this.getWorldContent()?.runAction(active.tour.onComplete);
      active.state = 'complete';
      this.syncLookFromEntity?.();
      this.syncUi();
    }
  }

  dispose(): void {
    this.clearLauncherEnter();
    this.stop(false);
    this.root.remove();
  }

  private start(tour: WorldFlythrough): void {
    this.clearLauncherEnter();
    if (this.active) this.stop(true);
    const cam = this.cameraEntity.camera;
    const savedPose: SavedCameraPose = {
      position: this.cameraEntity.getLocalPosition().clone(),
      rotation: this.cameraEntity.getLocalRotation().clone(),
      fov: cam?.fov,
    };
    this.active = {
      tour,
      state: 'playing',
      lastTickMs: performance.now(),
      elapsedMs: 0,
      durationMs: resolveFlythroughDurationMs(tour),
      firedCues: new Set(),
      savedPose,
      freeExploring: false,
    };
    this.renderedChapterKey = '';
    this.setMenuOpen(false);
    this.applySample(tour, 0);
    if (tour.onStart) this.getWorldContent()?.runAction(tour.onStart);
    this.onActiveChange?.(true);
    this.syncUi();
    this.requestRender(1);
  }

  private stop(restore: boolean): void {
    const active = this.active;
    if (!active) return;
    this.active = null;
    this.renderedChapterKey = '';
    this.clearLauncherEnter();
    this.launcherEntering = restore && this.chromeVisible && this.flythroughs.length > 0;
    if (active.tour.onStop) this.getWorldContent()?.runAction(active.tour.onStop);
    if (restore) this.restorePose(active.savedPose);
    this.onActiveChange?.(false);
    this.syncUi();
    if (this.launcherEntering) {
      this.launcherEnterTimer = window.setTimeout(() => {
        this.launcherEntering = false;
        this.launcherEnterTimer = null;
        this.syncUi();
      }, 520);
    }
    this.requestRender(1);
  }

  private clearLauncherEnter(): void {
    if (this.launcherEnterTimer !== null) {
      window.clearTimeout(this.launcherEnterTimer);
      this.launcherEnterTimer = null;
    }
    this.launcherEntering = false;
  }

  private pause(freeExploring = false): void {
    const active = this.active;
    if (!active || (active.state !== 'playing' && active.state !== 'returning')) return;
    active.state = 'paused';
    active.freeExploring = freeExploring;
    active.returnFrom = undefined;
    active.returnStartedAtMs = undefined;
    active.returnDurationMs = undefined;
    active.returnMode = undefined;
    this.syncLookFromEntity?.();
    this.syncUi();
    this.requestRender(1);
  }

  private resumeFromCurrentCamera(): void {
    const active = this.active;
    if (!active || active.state === 'playing' || active.state === 'returning') return;
    active.state = 'returning';
    active.freeExploring = false;
    active.returnFrom = this.readCameraPose();
    active.returnStartedAtMs = performance.now();
    active.returnDurationMs = PlayCanvasFlythroughController.RETURN_DURATION_MS;
    active.returnMode = 'play';
    this.syncUi();
    this.requestRender(1);
  }

  private replay(): void {
    const active = this.active;
    if (!active) return;
    active.elapsedMs = 0;
    active.firedCues.clear();
    active.state = 'returning';
    active.freeExploring = false;
    active.returnFrom = this.readCameraPose();
    active.returnStartedAtMs = performance.now();
    active.returnDurationMs = PlayCanvasFlythroughController.RETURN_DURATION_MS;
    active.returnMode = 'play';
    this.syncUi();
    this.requestRender(1);
  }

  private seekToChapter(chapter: ResolvedFlythroughChapter): void {
    const active = this.active;
    if (!active) return;
    if (chapter.timeMs < active.elapsedMs) active.firedCues.clear();
    active.elapsedMs = chapter.timeMs;
    active.state = 'returning';
    active.freeExploring = false;
    active.returnFrom = this.readCameraPose();
    active.returnStartedAtMs = performance.now();
    active.returnDurationMs = PlayCanvasFlythroughController.RETURN_DURATION_MS;
    active.returnMode = 'pause';
    this.syncUi();
    this.requestRender(1);
  }

  private seekToEnd(): void {
    const active = this.active;
    if (!active) return;
    active.elapsedMs = active.durationMs;
    active.state = 'returning';
    active.freeExploring = false;
    active.returnFrom = this.readCameraPose();
    active.returnStartedAtMs = performance.now();
    active.returnDurationMs = PlayCanvasFlythroughController.RETURN_DURATION_MS;
    active.returnMode = 'pause';
    this.syncUi();
    this.requestRender(1);
  }

  private handlePlayButton(): void {
    const state = this.active?.state;
    if (state === 'playing' || state === 'returning') {
      this.pause(false);
    } else if (state === 'complete') {
      this.replay();
    } else {
      this.resumeFromCurrentCamera();
    }
  }

  private restorePose(saved: SavedCameraPose): void {
    this.cameraEntity.setLocalPosition(saved.position);
    this.cameraEntity.setLocalRotation(saved.rotation);
    if (this.cameraEntity.camera && typeof saved.fov === 'number') {
      this.cameraEntity.camera.fov = saved.fov;
    }
    this.syncLookFromEntity?.();
  }

  private readCameraPose(): CameraReturnPose {
    const pos = this.cameraEntity.getLocalPosition();
    return {
      position: pos.clone(),
      rotation: this.cameraEntity.getLocalRotation().clone(),
      fov: this.cameraEntity.camera?.fov,
    };
  }

  private applyReturnMorph(
    from: CameraReturnPose,
    target: FlythroughSample,
    elapsedMs: number,
    durationMs: number,
  ): void {
    const t = smoothstep(clamp01(durationMs <= 0 ? 1 : elapsedMs / durationMs));
    const targetPosition = new pc.Vec3(target.position[0], target.position[1], target.position[2]);
    const position = new pc.Vec3().lerp(from.position, targetPosition, t);
    const targetRotation = new pc.Quat().setFromEulerAngles(
      target.rotation[0],
      target.rotation[1],
      target.rotation[2],
    );
    const rotation = new pc.Quat().slerp(from.rotation, targetRotation, t);

    this.cameraEntity.setLocalPosition(position);
    this.cameraEntity.setLocalRotation(rotation);
    if (!this.cameraEntity.camera) return;

    const fromFov = from.fov;
    const targetFov = target.fov;
    if (typeof fromFov === 'number' && typeof targetFov === 'number') {
      this.cameraEntity.camera.fov = lerp(fromFov, targetFov, t);
    } else if (typeof targetFov === 'number') {
      this.cameraEntity.camera.fov = targetFov;
    } else if (typeof fromFov === 'number') {
      this.cameraEntity.camera.fov = fromFov;
    }
  }

  private applyResolvedSample(sample: FlythroughSample): void {
    this.cameraEntity.setLocalPosition(sample.position[0], sample.position[1], sample.position[2]);
    this.cameraEntity.setLocalEulerAngles(
      sample.rotation[0],
      sample.rotation[1],
      sample.rotation[2],
    );
    if (this.cameraEntity.camera && typeof sample.fov === 'number') {
      this.cameraEntity.camera.fov = sample.fov;
    }
  }

  private applySample(tour: WorldFlythrough, elapsedMs: number): void {
    const sample = sampleFlythrough(tour, elapsedMs);
    this.applyResolvedSample(sample);
  }

  private renderMenu(): void {
    this.menuList.replaceChildren();
    for (const tour of this.flythroughs) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gii-flythrough-menu-item';
      btn.setAttribute('role', 'menuitem');
      const playIcon = document.createElement('span');
      playIcon.className = 'gii-flythrough-menu-play-icon';
      playIcon.setAttribute('aria-hidden', 'true');
      const label = document.createElement('span');
      label.textContent = tour.label;
      btn.append(playIcon, label);
      btn.addEventListener('click', () => this.start(tour));
      this.menuList.appendChild(btn);
    }
  }

  private setMenuOpen(open: boolean): void {
    const wasOpen = this.menuOpen;
    this.menuOpen = open && !this.active && this.flythroughs.length > 0;
    if (wasOpen && !this.menuOpen && this.menu.contains(document.activeElement)) {
      this.toggleBtn.focus({ preventScroll: true });
    }
    this.syncUi();
  }

  private renderChapters(active: ActiveFlythrough): void {
    const chapters = resolveFlythroughChapters(active.tour);
    const key = chapters.map((chapter) => `${chapter.id}:${chapter.timeMs}`).join('|');
    if (key === this.renderedChapterKey) return;
    this.renderedChapterKey = key;
    this.chaptersEl.replaceChildren();
    for (const chapter of chapters) {
      const edgeClass =
        chapter.progress <= 0.001
          ? ' gii-flythrough-chapter--start'
          : chapter.progress >= 0.999
            ? ' gii-flythrough-chapter--end'
            : '';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `gii-flythrough-chapter${edgeClass}`;
      btn.style.left = `${chapter.progress * 100}%`;
      btn.setAttribute('aria-label', `Jump to ${chapter.label}`);
      btn.innerHTML = `<span></span><em>${chapter.label}</em>`;
      btn.addEventListener('click', () => this.seekToChapter(chapter));
      this.chaptersEl.appendChild(btn);
    }
    const endBtn = document.createElement('button');
    endBtn.type = 'button';
    endBtn.className = 'gii-flythrough-chapter gii-flythrough-chapter--end';
    endBtn.style.left = '100%';
    endBtn.setAttribute('aria-label', 'Jump to end');
    endBtn.innerHTML = '<span></span><em>End</em>';
    endBtn.addEventListener('click', () => this.seekToEnd());
    this.chaptersEl.appendChild(endBtn);
  }

  private syncUi(): void {
    this.root.classList.toggle('gii-viewer-chrome--hidden', !this.chromeVisible);
    this.root.classList.toggle('gii-flythrough-ui--active', Boolean(this.active));
    this.root.classList.toggle('gii-flythrough-ui--menu-open', this.menuOpen);
    this.root.classList.toggle('gii-flythrough-ui--photo-active', this.photoModeActive);
    this.root.classList.toggle('gii-flythrough-ui--launcher-enter', this.launcherEntering);
    this.root.classList.toggle(
      'gii-flythrough-ui--pointer-help',
      Boolean(this.active && this.pointerLocked),
    );
    this.menu.setAttribute('aria-hidden', String(!this.menuOpen));
    this.toggleBtn.setAttribute('aria-expanded', String(this.menuOpen));
    this.toggleBtn.setAttribute('aria-pressed', String(this.menuOpen || Boolean(this.active)));
    this.toggleBtn.title = 'Guided tours';
    this.pointerHint.setAttribute('aria-hidden', String(!(this.active && this.pointerLocked)));
    this.status.hidden = !this.active;
    this.titleEl.hidden = !this.active;
    if (this.active) {
      this.titleEl.textContent = this.active.tour.label;
      this.stateLabel.textContent = this.photoModeActive
        ? 'Paused - photo mode active'
        : this.active.state === 'complete'
          ? 'Complete'
          : this.active.state === 'paused'
            ? this.active.freeExploring
              ? 'Paused - exploring'
              : 'Paused'
            : this.active.state === 'returning'
              ? 'Repositioning'
              : 'Playing';
      this.playBtn.textContent =
        this.active.state === 'complete'
          ? 'Replay'
          : this.active.state === 'paused'
            ? 'Play'
            : this.active.state === 'returning'
              ? 'Pause'
              : 'Pause';
      this.playBtn.title =
        this.active.state === 'complete'
          ? 'Replay tour'
          : this.active.state === 'paused'
            ? 'Resume tour'
            : 'Pause tour';
      this.renderChapters(this.active);
      this.updateProgress();
    } else {
      this.titleEl.textContent = '';
      this.renderedChapterKey = '';
      this.chaptersEl.replaceChildren();
    }
  }

  private updateProgress(): void {
    if (!this.active) {
      this.progress.style.width = '0%';
      return;
    }
    const pct = Math.max(0, Math.min(1, this.active.elapsedMs / this.active.durationMs));
    this.progress.style.width = `${pct * 100}%`;
    for (const chapterEl of this.chaptersEl.querySelectorAll<HTMLButtonElement>(
      '.gii-flythrough-chapter',
    )) {
      const left = Number.parseFloat(chapterEl.style.left) / 100;
      chapterEl.classList.toggle('gii-flythrough-chapter--active', Math.abs(left - pct) < 0.035);
    }
  }

  private static ensureStyles(): void {
    if (document.getElementById('gii-flythrough-css')) return;
    const style = document.createElement('style');
    style.id = 'gii-flythrough-css';
    style.textContent = `
.gii-flythrough-ui {
  --gii-chrome-outer-size: 2.91rem;
  --gii-chrome-inner-size: 2.35rem;
  --gii-chrome-outer-radius: 1.455rem;
  --gii-chrome-item-radius: 1.05rem;
  --gii-chrome-pad: 0.28rem;
  position: absolute;
  right: calc(0.78rem + env(safe-area-inset-right, 0px));
  top: calc(0.65rem + env(safe-area-inset-top, 0px));
  z-index: 70;
  display: grid;
  justify-items: end;
  gap: 0.45rem;
  width: min(20rem, calc(100vw - 2rem));
  color: rgba(255,255,255,0.92);
  pointer-events: none;
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}
.gii-flythrough-ui--active {
  left: 50%;
  right: auto;
  top: auto;
  bottom: calc(env(safe-area-inset-bottom, 0px) + clamp(0.85rem, 2.4vh, 1.45rem));
  width: min(44rem, calc(100vw - 2rem));
  height: auto;
  justify-items: center;
  transform: translateX(-50%);
}
.gii-flythrough-ui.gii-viewer-chrome--hidden {
  opacity: 0;
  transform: translateY(-0.4rem);
  pointer-events: none;
}
.gii-flythrough-ui--active.gii-viewer-chrome--hidden {
  transform: translateX(-50%) translateY(0.4rem);
}
.gii-flythrough-island,
.gii-flythrough-menu-item,
.gii-flythrough-play,
.gii-flythrough-exit {
  pointer-events: auto;
  border: 1px solid rgba(255,255,255,0.2);
  background: rgba(5, 10, 18, 0.62);
  color: inherit;
  backdrop-filter: blur(18px);
  box-shadow: 0 10px 34px rgba(0,0,0,0.24);
  cursor: pointer;
}
.gii-flythrough-island {
  position: relative;
  display: grid;
  grid-template-rows: var(--gii-chrome-inner-size) auto;
  align-items: start;
  justify-self: end;
  gap: 0;
  width: var(--gii-chrome-outer-size);
  max-width: min(20rem, calc(100vw - 2rem));
  min-height: var(--gii-chrome-outer-size);
  max-height: var(--gii-chrome-outer-size);
  padding: var(--gii-chrome-pad);
  overflow: hidden;
  border-radius: var(--gii-chrome-outer-radius);
  background: rgba(8, 16, 30, 0.82);
  border-color: rgba(255,255,255,0.16);
  box-shadow: 0 4px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06);
  transition:
    max-height 420ms cubic-bezier(0.22, 1, 0.36, 1) 160ms,
    min-height 420ms cubic-bezier(0.22, 1, 0.36, 1) 160ms,
    width 420ms cubic-bezier(0.22, 1, 0.36, 1) 580ms,
    border-color 220ms ease,
    background 220ms ease,
    box-shadow 220ms ease;
}
.gii-flythrough-ui--menu-open .gii-flythrough-island {
  width: min(20rem, calc(100vw - 2rem));
  min-height: 6.35rem;
  max-height: 16rem;
  background: rgba(8, 16, 30, 0.86);
  border-color: rgba(255,255,255,0.2);
  transition:
    width 420ms cubic-bezier(0.22, 1, 0.36, 1),
    min-height 680ms cubic-bezier(0.22, 1, 0.36, 1) 240ms,
    max-height 760ms cubic-bezier(0.22, 1, 0.36, 1) 240ms,
    border-color 220ms ease,
    background 220ms ease,
    box-shadow 220ms ease;
}
.gii-flythrough-toggle {
  position: absolute;
  top: calc(var(--gii-chrome-pad) - 1px);
  right: calc(var(--gii-chrome-pad) - 1px);
  z-index: 2;
  display: grid;
  place-items: center;
  width: var(--gii-chrome-inner-size);
  height: var(--gii-chrome-inner-size);
  pointer-events: auto;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: rgba(255,255,255,0.82);
  cursor: pointer;
  padding: 0;
  transition:
    color 160ms ease,
    box-shadow 160ms ease,
    transform 120ms ease;
}
.gii-flythrough-toggle::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: transparent;
  box-shadow: none;
  transition:
    background 160ms ease,
    box-shadow 160ms ease;
}
.gii-flythrough-icon {
  display: block;
  width: 1.28rem;
  height: 1.28rem;
  background: currentColor;
  -webkit-mask: url('/artwork/icon_movie-projector01.svg') center / contain no-repeat;
  mask: url('/artwork/icon_movie-projector01.svg') center / contain no-repeat;
  position: relative;
  z-index: 1;
  transform: translate(0.03rem, 0.02rem);
}
.gii-flythrough-toggle:hover,
.gii-flythrough-toggle:focus-visible {
  color: #fff;
}
.gii-flythrough-toggle:hover::before,
.gii-flythrough-toggle:focus-visible::before {
  background: rgba(255,255,255,0.1);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.08);
}
.gii-flythrough-ui--menu-open .gii-flythrough-toggle:hover::before,
.gii-flythrough-ui--menu-open .gii-flythrough-toggle:focus-visible::before {
  background: transparent;
  box-shadow: none;
}
.gii-flythrough-toggle:active { transform: scale(0.94); }
.gii-flythrough-toggle[aria-expanded='true'] {
  background: transparent;
  color: #fff;
  box-shadow: none;
}
.gii-flythrough-menu-item:hover {
  background: rgba(255,255,255,0.1);
  border-color: rgba(255,255,255,0.34);
  color: #fff;
}
.gii-flythrough-ui--active .gii-flythrough-toggle {
  display: none;
}
.gii-flythrough-ui--active .gii-flythrough-island {
  display: none;
}
.gii-flythrough-ui--launcher-enter .gii-flythrough-island {
  animation: gii-flythrough-launcher-enter 440ms cubic-bezier(0.22, 1, 0.36, 1) both;
}
.gii-flythrough-ui--menu-open {
  gap: 0;
  width: min(20rem, calc(100vw - 2rem));
}
.gii-flythrough-menu {
  grid-column: 1 / span 2;
  grid-row: 1 / span 2;
  display: grid;
  grid-template-rows: var(--gii-chrome-inner-size) auto;
  min-width: 0;
  pointer-events: none;
}
.gii-flythrough-menu-heading {
  grid-column: 1 / span 2;
  grid-row: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: var(--gii-chrome-inner-size);
  padding: 0 3.15rem 0 0.88rem;
  border-radius: var(--gii-chrome-item-radius);
  opacity: 0;
  transform: translateX(-0.28rem);
  font: 560 0.72rem/1 system-ui, -apple-system, Segoe UI, sans-serif;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(247,243,234,0.72);
  cursor: pointer;
  outline: none;
  transition:
    background 160ms ease,
    color 160ms ease,
    opacity 170ms ease 120ms,
    transform 170ms ease 120ms;
}
.gii-flythrough-ui--menu-open .gii-flythrough-menu-heading {
  opacity: 1;
  transform: translateX(0);
  transition-delay: 0ms, 0ms, 420ms, 420ms;
}
.gii-flythrough-ui--menu-open .gii-flythrough-menu-heading:hover,
.gii-flythrough-ui--menu-open .gii-flythrough-menu-heading:focus-visible {
  background: rgba(255,255,255,0.08);
  color: rgba(247,243,234,0.92);
}
.gii-flythrough-ui--menu-open .gii-flythrough-toggle:hover ~ .gii-flythrough-menu .gii-flythrough-menu-heading,
.gii-flythrough-ui--menu-open .gii-flythrough-toggle:focus-visible ~ .gii-flythrough-menu .gii-flythrough-menu-heading {
  background: rgba(255,255,255,0.08);
  color: rgba(247,243,234,0.92);
}
.gii-flythrough-menu-list {
  grid-column: 1 / span 2;
  grid-row: 2;
  display: grid;
  width: 100%;
  gap: 0.4rem;
  padding-top: 0.56rem;
  opacity: 0;
  transform: translateY(-0.2rem);
  pointer-events: none;
  transition:
    opacity 190ms ease,
    transform 190ms ease;
  transition-delay: 180ms;
}
.gii-flythrough-ui--menu-open .gii-flythrough-menu,
.gii-flythrough-ui--menu-open .gii-flythrough-menu-list {
  pointer-events: auto;
}
.gii-flythrough-ui--menu-open .gii-flythrough-menu-list {
  opacity: 1;
  transform: translateY(0);
  transition-delay: 620ms;
}
.gii-flythrough-menu-item {
  width: 100%;
  min-height: 2.8rem;
  border-radius: var(--gii-chrome-item-radius);
  padding: 0 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.62rem;
  text-align: left;
  font: 650 0.86rem/1.2 system-ui, -apple-system, Segoe UI, sans-serif;
  opacity: 0;
  transform: translateY(-0.16rem);
  transition:
    opacity 170ms ease,
    transform 170ms ease;
}
.gii-flythrough-menu-item:nth-child(1) {
  transition-delay: 90ms;
}
.gii-flythrough-menu-item:nth-child(2) {
  transition-delay: 45ms;
}
.gii-flythrough-menu-item:nth-child(3) {
  transition-delay: 0ms;
}
.gii-flythrough-ui--menu-open .gii-flythrough-menu-item {
  opacity: 1;
  transform: translateY(0);
}
.gii-flythrough-ui--menu-open .gii-flythrough-menu-item:nth-child(1) {
  transition-delay: 690ms;
}
.gii-flythrough-ui--menu-open .gii-flythrough-menu-item:nth-child(2) {
  transition-delay: 740ms;
}
.gii-flythrough-ui--menu-open .gii-flythrough-menu-item:nth-child(3) {
  transition-delay: 790ms;
}
.gii-flythrough-menu-play-icon {
  width: 0;
  height: 0;
  border-top: 0.31rem solid transparent;
  border-bottom: 0.31rem solid transparent;
  border-left: 0.48rem solid rgba(247,243,234,0.88);
  filter: drop-shadow(0 0 8px rgba(247,243,234,0.18));
}
.gii-flythrough-title {
  max-width: min(36rem, calc(100vw - 2rem));
  text-align: center;
  font: 700 0.72rem/1.2 system-ui, -apple-system, Segoe UI, sans-serif;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(247,243,234,0.84);
  text-shadow: 0 8px 28px rgba(0,0,0,0.55);
  pointer-events: none;
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}
.gii-flythrough-title[hidden] { display: none; }
.gii-flythrough-ui--photo-active .gii-flythrough-title {
  opacity: 0;
  transform: translateY(0.3rem);
}
.gii-flythrough-status {
  width: min(44rem, calc(100vw - 2rem));
  display: grid;
  gap: 0.62rem;
  padding: 0.78rem 1rem;
  border: 1px solid rgba(255,255,255,0.16);
  border-radius: var(--gii-chrome-outer-radius);
  background: rgba(5,10,18,0.55);
  backdrop-filter: blur(18px);
  box-shadow: 0 18px 50px rgba(0,0,0,0.28);
  pointer-events: auto;
  transition:
    padding 240ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 240ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
}
.gii-flythrough-status[hidden] { display: none; }
.gii-flythrough-controls {
  display: grid;
  grid-template-columns: minmax(5.6rem, auto) minmax(14rem, 1fr) minmax(5.6rem, auto);
  align-items: center;
  gap: calc(0.85rem + 10px);
  transition: opacity 180ms ease;
}
.gii-flythrough-ui--photo-active .gii-flythrough-status {
  transform: translateY(calc(100% - 0.5rem));
}
.gii-flythrough-ui--photo-active .gii-flythrough-controls,
.gii-flythrough-ui--photo-active .gii-flythrough-pointer-hint {
  opacity: 0;
  pointer-events: none;
}
.gii-flythrough-state {
  justify-self: center;
  font: 650 0.68rem/1 system-ui, -apple-system, Segoe UI, sans-serif;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(247,243,234,0.72);
}
.gii-flythrough-play,
.gii-flythrough-exit {
  min-width: 4.5rem;
  height: 2.15rem;
  border-radius: 999px;
  padding: 0 0.78rem;
  font: 750 0.68rem/1 system-ui, -apple-system, Segoe UI, sans-serif;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.gii-flythrough-play {
  background: rgba(247,243,234,0.9);
  color: #071018;
  border-color: rgba(247,243,234,0.75);
}
.gii-flythrough-play:hover,
.gii-flythrough-exit:hover {
  background: rgba(255,255,255,0.16);
  border-color: rgba(255,255,255,0.36);
}
.gii-flythrough-play:hover {
  background: rgba(247,243,234,1);
}
.gii-flythrough-timeline {
  position: relative;
  height: 2.4rem;
  min-width: 12rem;
}
.gii-flythrough-progress {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 3px;
  transform: translateY(-50%);
  border-radius: 999px;
  background: rgba(255,255,255,0.2);
  overflow: hidden;
}
.gii-flythrough-progress span {
  display: block;
  width: 0%;
  height: 100%;
  border-radius: inherit;
  background: rgba(247,243,234,0.92);
}
.gii-flythrough-chapters {
  position: absolute;
  inset: 0;
}
.gii-flythrough-chapter {
  position: absolute;
  top: 50%;
  width: 1.05rem;
  height: 1.05rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: rgba(247,243,234,0.94);
  transform: translate(-50%, -50%);
  cursor: pointer;
}
.gii-flythrough-chapter span {
  position: absolute;
  inset: 0.28rem;
  border-radius: 999px;
  background: currentColor;
  box-shadow: 0 0 0 1px rgba(7,16,24,0.88), 0 0 0 4px rgba(247,243,234,0.12);
  transition: inset 160ms ease, box-shadow 160ms ease, opacity 160ms ease;
}
.gii-flythrough-chapter em {
  position: absolute;
  left: 50%;
  bottom: calc(50% + 0.82rem);
  transform: translateX(-50%) translateY(0.18rem);
  opacity: 0;
  white-space: nowrap;
  pointer-events: none;
  font: 650 0.62rem/1 system-ui, -apple-system, Segoe UI, sans-serif;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(247,243,234,0.82);
  transition: opacity 160ms ease, transform 160ms ease;
}
.gii-flythrough-chapter--start em {
  left: 0;
  transform: translateX(0) translateY(0.18rem);
}
.gii-flythrough-chapter--end em {
  left: auto;
  right: 0;
  transform: translateX(0) translateY(0.18rem);
}
.gii-flythrough-chapter:hover span,
.gii-flythrough-chapter--active span {
  inset: 0.18rem;
  box-shadow: 0 0 0 1px rgba(7,16,24,0.88), 0 0 0 6px rgba(247,243,234,0.18);
}
.gii-flythrough-chapter:hover em,
.gii-flythrough-chapter:focus-visible em {
  opacity: 1;
  transform: translateX(-50%) translateY(-0.08rem);
}
.gii-flythrough-chapter--start:hover em,
.gii-flythrough-chapter--start:focus-visible em,
.gii-flythrough-chapter--end:hover em,
.gii-flythrough-chapter--end:focus-visible em {
  transform: translateX(0) translateY(-0.08rem);
}
.gii-flythrough-pointer-hint {
  max-height: 0;
  margin-top: -0.62rem;
  overflow: hidden;
  opacity: 0;
  font: 650 0.56rem/1 system-ui, -apple-system, Segoe UI, sans-serif;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(247,243,234,0.5);
  text-align: center;
  text-shadow: 0 7px 24px rgba(0,0,0,0.5);
  pointer-events: none;
  transition:
    max-height 240ms cubic-bezier(0.22, 1, 0.36, 1),
    margin 240ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 160ms ease;
}
.gii-flythrough-ui--pointer-help .gii-flythrough-status {
  padding-bottom: 0.94rem;
}
.gii-flythrough-ui--pointer-help .gii-flythrough-pointer-hint {
  max-height: 1rem;
  margin-top: -0.12rem;
  opacity: 1;
}
.gii-flythrough-ui--photo-active .gii-flythrough-pointer-hint {
  max-height: 0;
  margin-top: -0.62rem;
  opacity: 0;
}
@keyframes gii-flythrough-launcher-enter {
  from {
    opacity: 0;
    transform: translateX(calc(var(--gii-chrome-outer-size) + 2rem));
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
@media (max-width: 640px) {
  .gii-flythrough-ui {
    right: 0.75rem;
    top: calc(env(safe-area-inset-top, 0px) + 0.75rem);
  }
  .gii-flythrough-ui--active {
    left: 50%;
    right: auto;
    top: auto;
    bottom: calc(env(safe-area-inset-bottom, 0px) + 1rem);
    width: min(29rem, calc(100vw - 1rem));
    transform: translateX(-50%);
  }
  .gii-flythrough-ui.gii-viewer-chrome--hidden {
    transform: translateY(-0.4rem);
  }
  .gii-flythrough-ui--active.gii-viewer-chrome--hidden {
    transform: translateX(-50%) translateY(0.4rem);
  }
  .gii-flythrough-status {
    width: min(29rem, calc(100vw - 1rem));
    padding: 0.6rem;
  }
  .gii-flythrough-controls {
    grid-template-columns: auto minmax(7rem, 1fr) auto;
    gap: 0.45rem;
  }
  .gii-flythrough-timeline {
    min-width: 7rem;
  }
  .gii-flythrough-play,
  .gii-flythrough-exit {
    min-width: 3.75rem;
    padding: 0 0.6rem;
    font-size: 0.62rem;
  }
  .gii-flythrough-state {
    justify-self: center;
  }
  .gii-flythrough-chapter em {
    display: none;
  }
}
`;
    document.head.appendChild(style);
  }

  private static iconTours(): string {
    return `<span class="gii-flythrough-icon" aria-hidden="true"></span>`;
  }
}
